import React, { useState, useEffect, useRef } from 'react';
import { Col, Row } from 'reactstrap';
import { withStyles } from '@material-ui/core';
import { Icon } from 'semantic-ui-react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Constants from '../Pages/Constants';
import DataServiceSQL from '../DataService/DataServiceSQL';
import Utils from '../Components/Common/Utils.js';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Each feature carries the bit number it occupies in the subscription bitmask.
const FEATURE_BITS = Utils.FEATURE_NAMES.map((name, bit) => ({ name, bit }));

// Active features of a bitmask, as [{ name, bit }], for display "name (bit)".
const getActiveFeatures = (features) => {
  if (features == null) return [];
  const bits = Number(features);
  return FEATURE_BITS.filter(({ bit }) => (bits >> bit) & 1);
};

const SubscriptionManagementMenu = (props) => {
  const { classes } = props;
  const intl = useIntl();

  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [allStructures, setAllStructures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Search / filter / pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const allSubsRef = useRef([]);

  // Create/edit form state
  const [selectedFeatures, setSelectedFeatures] = useState({}); // { [bit]: true }
  const [subTotal, setSubTotal] = useState(1);
  const [subUsed, setSubUsed] = useState(0);
  const [expirationDate, setExpirationDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState("");
  const [copiedSubId, setCopiedSubId] = useState(null);

  const highlightRowRef = useRef(null);

  useEffect(() => {
    loadSubscriptions();
    loadAllStructures();
  }, []);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await DataServiceSQL.getAllSubscriptions();
      const data = response.data || [];
      allSubsRef.current = data;
      setSubscriptions(data);
      applySubFilters(searchQuery, statusFilter, data);
    } catch (e) {
      allSubsRef.current = [];
      setSubscriptions([]);
      setFilteredSubscriptions([]);
    }
    setLoading(false);
  };

  const applySubFilters = (query, status, dataOverride = null) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let result = dataOverride !== null ? dataOverride : allSubsRef.current;
    if (status === "active") {
      result = result.filter(s => !s.expiration_date || new Date(s.expiration_date) > today);
    } else if (status === "expired") {
      result = result.filter(s => s.expiration_date && new Date(s.expiration_date) <= today);
    } else if (status === "saturated") {
      result = result.filter(s => (s.sub_used ?? 0) >= (s.sub_total ?? 0));
    } else if (status === "available") {
      result = result.filter(s => (s.sub_used ?? 0) < (s.sub_total ?? 0));
    }
    if (query.trim().length > 0) {
      const q = query.toLowerCase();
      result = result.filter(s =>
        (s.id_subscription && s.id_subscription.toLowerCase().includes(q)) ||
        (structuresBySubscription[s.id_subscription] || []).some(
          str => str.structure_name && str.structure_name.toLowerCase().includes(q)
        )
      );
    }
    setFilteredSubscriptions(result);
    setCurrentPage(1);
  };

  const onStatusFilterChange = (status) => {
    setStatusFilter(status);
    applySubFilters(searchQuery, status);
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value === "") applySubFilters("", statusFilter);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter') applySubFilters(searchQuery, statusFilter);
  };

  const applySearch = () => applySubFilters(searchQuery, statusFilter);

  const clearSearch = () => {
    setSearchQuery("");
    applySubFilters("", statusFilter);
  };

  const totalPages = Math.max(1, Math.ceil(filteredSubscriptions.length / pageSize));
  const paginatedSubscriptions = filteredSubscriptions.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const loadAllStructures = async () => {
    try {
      const res = await DataServiceSQL.getAllStructures();
      setAllStructures(res.data || []);
    } catch (e) {
      setAllStructures([]);
    }
  };

  // Group structures by FK_subscription
  const structuresBySubscription = allStructures.reduce((acc, s) => {
    if (s.FK_subscription) {
      if (!acc[s.FK_subscription]) acc[s.FK_subscription] = [];
      acc[s.FK_subscription].push(s);
    }
    return acc;
  }, {});

  // Pre-filter + scroll when opened from structures page with a specific subscription
  useEffect(() => {
    if (props.highlightId && allSubsRef.current.length > 0) {
      setSearchQuery(props.highlightId);
      const filtered = allSubsRef.current.filter(s =>
        s.id_subscription && s.id_subscription.toLowerCase().includes(props.highlightId.toLowerCase())
      );
      setFilteredSubscriptions(filtered);
      setCurrentPage(1);
    }
    if (props.highlightId && highlightRowRef.current) {
      highlightRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // eslint-disable-next-line
  }, [props.highlightId, subscriptions]);

  // Bitmask built from the currently checked features.
  const computedFeatures = FEATURE_BITS.reduce(
    (acc, { bit }) => (selectedFeatures[bit] ? acc + Math.pow(2, bit) : acc),
    0
  );

  const toggleFeature = (bit) => {
    setSelectedFeatures(prev => ({ ...prev, [bit]: !prev[bit] }));
  };

  const openAddForm = () => {
    setSelectedFeatures({});
    setSubTotal(1);
    setSubUsed(0);
    setExpirationDate("");
    setAddError("");
    setEditingId(null);
    setShowAddForm(true);
  };

  const openEditForm = (sub) => {
    const bits = Number(sub.features) || 0;
    const featureMap = {};
    FEATURE_BITS.forEach(({ bit }) => { if ((bits >> bit) & 1) featureMap[bit] = true; });
    setSelectedFeatures(featureMap);
    setSubTotal(sub.sub_total ?? 1);
    setSubUsed(sub.sub_used ?? 0);
    setExpirationDate(sub.expiration_date ? sub.expiration_date.slice(0, 10) : "");
    setAddError("");
    setEditingId(sub.id_subscription);
    setShowAddForm(true);
  };

  const saveSubscription = async () => {
    setAddError("");
    setSaving(true);
    try {
      const payload = {
        features: computedFeatures,
        sub_total: Number(subTotal) || 0,
        sub_used: Number(subUsed) || 0,
        expiration_date: expirationDate || null,
      };
      if (editingId) {
        await DataServiceSQL.updateSubscription(editingId, payload);
      } else {
        await DataServiceSQL.createSubscription(payload);
      }
      setShowAddForm(false);
      setEditingId(null);
      loadSubscriptions();
      loadAllStructures();
    } catch (e) {
      setAddError(intl.formatMessage({ id: 'message.error', defaultMessage: 'An error occurred' }));
    }
    setSaving(false);
  };

  const renderFeatureChips = (features) => {
    const active = getActiveFeatures(features);
    if (active.length === 0) return <span style={{ color: "#bbb" }}>—</span>;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
        {active.map(({ name, bit }) => (
          <span
            key={bit}
            style={{ background: "#ebf5fb", border: "1px solid #aed6f1", borderRadius: "10px", padding: "1px 8px", color: "#2980b9", fontSize: "0.82em", whiteSpace: "nowrap" }}
          >
            {name} <span style={{ color: "#7f8c8d", fontWeight: 600 }}>({bit})</span>
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className={classes.popup_background} onClick={props.cancelPopup}>
      <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
        <div className="management-menu-popup" style={{ height: "auto", maxHeight: "92vh", overflowY: "auto", position: "relative" }}>

          {/* Close button top-right */}
          <button
            onClick={props.cancelPopup}
            style={{ position: 'absolute', top: 6, right: 20, zIndex: 10, background: '#fff', border: '1px solid #ccc', borderRadius: '50%', fontSize: 18, fontWeight: 'bold', lineHeight: 1, cursor: 'pointer', color: '#DB2828', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
            title={intl.formatMessage({ id: 'button.close', defaultMessage: 'Fermer' })}
          >×</button>

          {/* Title */}
          <Row style={{ paddingBottom: "20px", alignItems: "center" }}>
            <Col style={{ fontWeight: "bold", fontSize: "2.2em", textAlign: "center" }}>
              <FormattedMessage id="subscription.title" defaultMessage="Abonnements" />
            </Col>
          </Row>

          {showAddForm ? (
            /* ---- Create subscription form ---- */
            <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "4px 0" }}>
              <div style={{ width: "100%", maxWidth: "640px", background: "#fff", borderRadius: "12px", boxShadow: "0 4px 24px rgba(0,0,0,0.10)", padding: "28px 32px" }}>

                <div style={{ display: "flex", alignItems: "center", marginBottom: "22px", borderBottom: "2px solid #f0f0f0", paddingBottom: "14px" }}>
                  <Icon name="tag" size="large" color="blue" />
                  <h5 style={{ margin: "0 0 0 10px", fontWeight: 600, color: "#2c3e50" }}>
                    {editingId
                      ? <FormattedMessage id="subscription.edit" defaultMessage="Modifier l'abonnement" />
                      : <FormattedMessage id="subscription.add" defaultMessage="Créer un abonnement" />}
                  </h5>
                </div>

                {addError && <div className="alert alert-danger" style={{ borderRadius: "8px", fontSize: "0.9em" }}>{addError}</div>}

                {/* Capacity + expiration */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "20px" }}>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: "0.85em", color: "#444", marginBottom: "4px", display: "block" }}>
                      <FormattedMessage id="subscription.total" defaultMessage="Nombre total" />
                    </label>
                    <input type="number" min="0" className="form-control" value={subTotal} onChange={e => setSubTotal(e.target.value)} style={{ borderRadius: "7px" }} />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: "0.85em", color: "#444", marginBottom: "4px", display: "block" }}>
                      <FormattedMessage id="subscription.used" defaultMessage="Utilisés" />
                    </label>
                    <input type="number" min="0" className="form-control" value={subUsed} onChange={e => setSubUsed(e.target.value)} style={{ borderRadius: "7px" }} />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: "0.85em", color: "#444", marginBottom: "4px", display: "block" }}>
                      <FormattedMessage id="subscription.expiration" defaultMessage="Expiration" />
                    </label>
                    <input type="date" className="form-control" value={expirationDate} onChange={e => setExpirationDate(e.target.value)} style={{ borderRadius: "7px" }} />
                  </div>
                </div>

                {/* Features checkboxes */}
                <p style={{ fontSize: "0.75em", fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: "0.08em", marginBottom: "10px" }}>
                  <FormattedMessage id="subscription.features" defaultMessage="Fonctionnalités" />
                  <span style={{ marginLeft: "8px", color: "#2980b9", fontWeight: 600 }}>
                    {intl.formatMessage({ id: 'subscription.bitmask', defaultMessage: 'Valeur' })}: {computedFeatures}
                  </span>
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", marginBottom: "22px" }}>
                  {FEATURE_BITS.map(({ name, bit }) => (
                    <label key={bit} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", userSelect: "none", fontSize: "0.88em", color: "#444" }}>
                      <input
                        type="checkbox"
                        checked={!!selectedFeatures[bit]}
                        onChange={() => toggleFeature(bit)}
                        style={{ width: 15, height: 15, cursor: "pointer" }}
                      />
                      <span>{name} <span style={{ color: "#7f8c8d", fontWeight: 600 }}>({bit})</span></span>
                    </label>
                  ))}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", borderTop: "1px solid #f0f0f0", paddingTop: "16px" }}>
                  <button className="btn btn-outline-secondary" onClick={() => { setShowAddForm(false); setEditingId(null); }} style={{ borderRadius: "7px", minWidth: "90px" }}>
                    <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
                  </button>
                  <button className="btn btn-primary" onClick={saveSubscription} disabled={saving} style={{ borderRadius: "7px", minWidth: "110px", fontWeight: 600 }}>
                    {saving
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : <><Icon name="save outline" /><FormattedMessage id="button.save" defaultMessage="Save" /></>
                    }
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* ---- Subscription list ---- */
            <>
              {/* Back to structures button (only when opened from structures page) */}
              {props.highlightId && props.onOpenStructures && (
                <Row style={{ marginBottom: "8px", alignItems: "center" }}>
                  <Col style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => props.onOpenStructures(props.highlightId)}
                      style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "0.85em", borderRadius: "6px", padding: "3px 10px" }}
                      title={intl.formatMessage({ id: 'subscription.backToStructures', defaultMessage: 'Retour aux structures' })}
                    >
                      <Icon name="arrow left" style={{ margin: 0 }} />
                      <FormattedMessage id="subscription.backToStructures" defaultMessage="Structures" />
                    </button>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#fef9e7", border: "1px solid #f9e79f", borderRadius: "12px", padding: "3px 10px", fontSize: "0.85em", color: "#9a7d0a", fontWeight: 600 }}>
                      <Icon name="tag" style={{ margin: 0 }} />
                      <span style={{ fontFamily: "monospace" }}>{props.highlightId.slice(0, 8)}…</span>
                    </span>
                  </Col>
                </Row>
              )}

              {/* Status filter + count row */}
              <Row style={{ marginBottom: "8px", flexWrap: "wrap", alignItems: "center" }}>
                <Col>
                  {[
                    { label: intl.formatMessage({ id: 'subscription.filter.all', defaultMessage: 'Tous' }), value: "" },
                    { label: intl.formatMessage({ id: 'subscription.filter.active', defaultMessage: 'Actifs' }), value: "active" },
                    { label: intl.formatMessage({ id: 'subscription.filter.expired', defaultMessage: 'Expirés' }), value: "expired" },
                    { label: intl.formatMessage({ id: 'subscription.filter.saturated', defaultMessage: 'Saturés' }), value: "saturated" },
                    { label: intl.formatMessage({ id: 'subscription.filter.available', defaultMessage: 'Disponibles' }), value: "available" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      className={"btn btn-sm " + (statusFilter === opt.value ? "btn-primary" : "btn-outline-secondary")}
                      style={{ marginRight: "4px", marginBottom: "4px" }}
                      onClick={() => onStatusFilterChange(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                  <span style={{ marginLeft: "8px", fontSize: "0.9em", color: "#555", verticalAlign: "middle" }}>
                    {(searchQuery.length > 0 || statusFilter !== "")
                      ? <><strong>{filteredSubscriptions.length}</strong> <FormattedMessage id="structure.results" defaultMessage="résultats" /></>
                      : <><strong>{subscriptions.length}</strong> <FormattedMessage id="subscription.title" defaultMessage="Abonnements" /></>
                    }
                  </span>
                </Col>
              </Row>

              {/* Search + Add row */}
              <Row style={{ marginBottom: "10px", flexWrap: "nowrap", alignItems: "center" }}>
                <Col sm={8} style={{ width: "65%" }}>
                  <Row style={{ flexWrap: "nowrap", alignItems: "center" }}>
                    <div style={{ position: "relative", width: "auto", maxWidth: "80%" }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={intl.formatMessage({ id: 'subscription.search', defaultMessage: 'Rechercher par ID ou structure…' })}
                        value={searchQuery}
                        onChange={onSearchChange}
                        onKeyDown={onSearchKeyDown}
                        style={{ paddingRight: searchQuery.length > 0 ? "28px" : undefined }}
                      />
                      {searchQuery.length > 0 && (
                        <span
                          onClick={clearSearch}
                          style={{ position: "absolute", right: "20px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#999", fontSize: "1.1em", lineHeight: 1 }}
                        >&times;</span>
                      )}
                    </div>
                    <button className="btn btn-outline-secondary" onClick={applySearch} style={{ width: "auto", marginLeft: "4px" }}>
                      <Icon name='search' size='large' inverted color='black' />
                    </button>
                  </Row>
                </Col>
                <Col sm={4} style={{ width: "35%", display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: "center", gap: "6px" }}>
                  <span style={{ fontSize: "0.85em", whiteSpace: "nowrap", color: "#555" }}><FormattedMessage id="structure.pagination" defaultMessage="Pagination" /></span>
                  <select className="form-control" style={{ width: "80px" }} value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                    {PAGE_SIZE_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <button className="btn btn-sm btn-success" onClick={openAddForm} style={{ whiteSpace: "nowrap", fontSize: "0.9em" }}>
                    <Icon name="plus" /><FormattedMessage id="subscription.add" defaultMessage="Créer un abonnement" />
                  </button>
                </Col>
              </Row>

              {/* Pagination top */}
              <Row style={{ alignItems: "center", justifyContent: "center", padding: "4px 0" }}>
                <Col style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>&laquo;</button>
                  <span>{currentPage} / {totalPages}</span>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>&raquo;</button>
                </Col>
              </Row>

              <Row style={{ overflowY: "auto", height: "65%" }}>
                {loading ? (
                  <center><p><FormattedMessage id="loading" defaultMessage="Loading..." /></p></center>
                ) : (
                  <table className="table table-striped table-bordered" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th><FormattedMessage id="subscription.id" defaultMessage="ID" /></th>
                        <th><FormattedMessage id="subscription.usage" defaultMessage="Utilisés / Total" /></th>
                        <th><FormattedMessage id="subscription.expiration" defaultMessage="Expiration" /></th>
                        <th><FormattedMessage id="subscription.features" defaultMessage="Fonctionnalités" /></th>
                        <th><FormattedMessage id="subscription.structures" defaultMessage="Structures" /></th>
                        <th style={{ width: 60 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSubscriptions.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: "center" }}>
                            <FormattedMessage id="table.nodata" defaultMessage="No data" />
                          </td>
                        </tr>
                      ) : (
                        paginatedSubscriptions.map((sub, idx) => (
                          <tr
                            key={sub.id_subscription || idx}
                            ref={props.highlightId === sub.id_subscription ? highlightRowRef : null}
                            style={props.highlightId === sub.id_subscription ? { background: "#fffbcc", outline: "2px solid #f1c40f" } : {}}
                          >
                            <td style={{ fontSize: "0.82em", fontFamily: "monospace", whiteSpace: "nowrap" }} title={sub.id_subscription}>
                              {sub.id_subscription ? sub.id_subscription.slice(0, 8) + "…" : "—"}
                              {sub.id_subscription && (
                                <button
                                  className="btn btn-sm"
                                  style={{ padding: "0 4px", border: "none", background: "transparent", color: copiedSubId === sub.id_subscription ? "#27ae60" : "#aaa", lineHeight: 1, marginLeft: "2px" }}
                                  title={intl.formatMessage({ id: 'subscription.copyId', defaultMessage: 'Copier l\'ID' })}
                                  onClick={() => {
                                    navigator.clipboard.writeText(sub.id_subscription).then(() => {
                                      setCopiedSubId(sub.id_subscription);
                                      setTimeout(() => setCopiedSubId(prev => prev === sub.id_subscription ? null : prev), 1500);
                                    });
                                  }}
                                >
                                  <Icon name={copiedSubId === sub.id_subscription ? "check" : "copy outline"} style={{ margin: 0 }} />
                                </button>
                              )}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <span style={{ fontWeight: 600, color: sub.sub_used >= sub.sub_total ? "#c0392b" : "#27ae60" }}>
                                {sub.sub_used} / {sub.sub_total}
                              </span>
                            </td>
                            <td style={{ whiteSpace: "nowrap", fontSize: "0.85em" }}>
                              {sub.expiration_date ? new Date(sub.expiration_date).toLocaleDateString() : <span style={{ color: "#bbb" }}>—</span>}
                            </td>
                            <td>{renderFeatureChips(sub.features)}</td>
                            <td style={{ fontSize: "0.82em", minWidth: "160px" }}>
                              {(() => {
                                const structs = structuresBySubscription[sub.id_subscription] || [];
                                if (structs.length === 0) return <span style={{ color: "#bbb" }}>—</span>;
                                return (
                                  <div>
                                    <div style={{ marginBottom: "4px" }}>
                                      {structs.map(s => (
                                        <div key={s.id_structure} style={{ whiteSpace: "nowrap", color: "#34495e" }}>
                                          <Icon name="hospital outline" style={{ margin: "0 3px 0 0", color: "#7f8c8d" }} />
                                          {s.structure_name}
                                        </div>
                                      ))}
                                    </div>
                                    {props.onOpenStructures && (
                                      <button
                                        className="btn btn-sm btn-outline-secondary"
                                        style={{ fontSize: "0.8em", padding: "1px 7px", marginTop: "2px" }}
                                        onClick={() => props.onOpenStructures(sub.id_subscription)}
                                        title={intl.formatMessage({ id: 'subscription.seeStructures', defaultMessage: 'Voir dans la page Structures' })}
                                      >
                                        <Icon name="external alternate" style={{ margin: "0 3px 0 0" }} />
                                        <FormattedMessage id="subscription.seeStructures" defaultMessage="Voir les structures" />
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                style={{ padding: "2px 8px", fontSize: "0.8em" }}
                                onClick={() => openEditForm(sub)}
                                title={intl.formatMessage({ id: 'button.edit', defaultMessage: 'Modifier' })}
                              >
                                <Icon name="pencil" style={{ margin: 0 }} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </Row>

              {/* Pagination bottom */}
              <Row style={{ alignItems: "center", justifyContent: "center", padding: "6px 0" }}>
                <Col style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>&laquo;</button>
                  <span>{currentPage} / {totalPages}</span>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>&raquo;</button>
                </Col>
              </Row>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default withStyles(Constants.styles)(SubscriptionManagementMenu);
